import { useRouter } from "next/router";
import { useEffect, useState, useCallback } from "react";

import { NonprofitAdminReferralCampaignResponse } from "@every.org/common/src/codecs/entities";
import {
  getRoutePath,
  ClientRouteName,
  URLFormat,
} from "@every.org/common/src/helpers/clientRoutes";
import { getReferralCampaignSpec } from "@every.org/common/src/routes/referralCampaign";

import { NonprofitSearchPage } from "src/components/Nonprofit/NonprofitSearchPage";
import { setAdminReferralCookie } from "src/context/AuthContext/actions";
import { nonprofitOrUndefined } from "src/context/NonprofitsContext/selectors";
import { ContextNonprofit } from "src/context/NonprofitsContext/types";
import { useAsyncEffect } from "src/hooks/useAsyncEffect";
import { useUserLayer } from "src/utility/abtesting";
import { queryApi } from "src/utility/apiClient";
import { logger } from "src/utility/logger";

const NonprofitOnboardingSearchPage = ({ referral }: { referral?: string }) => {
  const { push } = useRouter();
  const [campaign, setCampaign] =
    useState<NonprofitAdminReferralCampaignResponse>();

  const layer = useUserLayer("np_onboarding", false);
  const mainCardText = layer.get<string>("h", "Claim your nonprofit");

  useEffect(() => {
    if (referral) {
      setAdminReferralCookie({ referral });
    }
  }, [referral]);

  useAsyncEffect({
    asyncOperation: useCallback(() => {
      if (referral) {
        return queryApi(getReferralCampaignSpec, {
          routeTokens: { referralCode: referral },
          queryParams: {},
          body: {},
        });
      }
      return Promise.resolve(null);
    }, [referral]),
    handleResponse: useCallback(
      (response: NonprofitAdminReferralCampaignResponse | null) => {
        if (response) {
          setCampaign(response);
        }
      },
      [setCampaign]
    ),
    handleError: useCallback(
      (error) => {
        logger.error({
          error,
          message: `Error fetching referral campaign via referral code`,
          data: { referralCode: referral },
        });
        setCampaign(undefined);
      },
      [referral]
    ),
  });

  const onButtonClick = (nonprofit: ContextNonprofit) => {
    push(
      getRoutePath({
        name: ClientRouteName.NONPROFITS_ONBOARDING_INTRO,
        format: URLFormat.RELATIVE,
        tokens: {
          nonprofitSlug:
            nonprofitOrUndefined(nonprofit)?.primarySlug || "UNKNOWN",
        },
        query: referral ? { referral } : {},
      })
    );
  };

  return (
    <NonprofitSearchPage
      pageTitle={"Let's get started!"}
      mainCardText={mainCardText}
      ctaText={"Continue"}
      linkText={"Learn how we help nonprofits fundraise"}
      linkUrl={"https://www.every.org/demos"}
      placeholder={"Search by your nonprofit's EIN or name"}
      onButtonClick={onButtonClick}
      campaign={campaign}
      showPageHeader
    />
  );
};

export default NonprofitOnboardingSearchPage;
